import { useState, useEffect, Fragment } from 'react'
import AudioPlayerWithAsset from './AudioPlayerWithAsset';
import VoiceRecordsList from './VoiceRecordsList';
import { fetchAudioAsBinary, getVoiceName } from '../../utils/voiceUtils';
import { getUserData } from '../../utils/auth';
import { apiRequest, trickleCreateObject, trickleGetObject, trickleUpdateObject } from '../../utils/api';

function RewriteResultItem({ 
    result, 
    index, 
    isEditing, 
    editedContent, 
    isSaving,
    onEditToggle, 
    onContentChange, 
    onSave, 
    onCopy,
    openVoiceSettingsModal,
    openVoiceModal,
    hasApiKey,
    showAvatarGenerateModel, currentPlayingId, isPlaying, onPlayVoice
}) {

    const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);
    const [assetId, setAssetId] = useState(null);
    const [generatedVoices, setGeneratedVoices] = useState(result.voices);
    const [selectedVoiceForAvatar, setSelectedVoiceForAvatar] = useState(null);
    const [isSelectingVoice, setIsSelectingVoice] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [isPlayingExistingVoice, setIsPlayingExistingVoice] = useState(false);
    const [currentVoiceSettings, setCurrentVoiceSettings] = useState(null);

    // Function to handle voice selection for avatar
    const handleVoiceSelect = (voiceId) => {
        if (isSelectingVoice) {
            setSelectedVoiceForAvatar(voiceId === selectedVoiceForAvatar ? null : voiceId);
        }
    };

    // Function to cancel voice selection
    const cancelVoiceSelection = () => {
        setIsSelectingVoice(false);
        setSelectedVoiceForAvatar(null);
    };

    const generateDigitalAvatar = async (e) => {
        // Prevent event propagation
        e.preventDefault();
        e.stopPropagation();
        
        // If we don't have any generated voices, show error
        if (generatedVoices.length === 0) {
            window.dispatchEvent(new CustomEvent('showMessage', {
                detail: { type: 'error', text: '請先生成語音檔案' }
            }));
            return;
        }
        
        // If we have voice records but none is selected and we're not in selection mode,
        // enter selection mode
        if (generatedVoices.length > 0 && !selectedVoiceForAvatar && !isSelectingVoice) {
            setIsSelectingVoice(true);
            window.dispatchEvent(new CustomEvent('showMessage', {
                detail: { type: 'info', text: '請選擇一個語音記錄用於生成數字人' }
            }));
            return;
        }
        
        // If no voice is selected even after entering selection mode, show error
        if (!selectedVoiceForAvatar && isSelectingVoice) {
            window.dispatchEvent(new CustomEvent('showMessage', {
                detail: { type: 'error', text: '請選擇一個語音記錄' }
            }));
            return;
        }
        
        // Check if API key is available
        if (!hasApiKey) {
            window.dispatchEvent(new CustomEvent('showMessage', {
                detail: { type: 'error', text: '請先在 語音記錄 頁面輸入 API Key' }
            }));
            return;
        }
        
        setIsGeneratingAvatar(true);
        
        try {
            // Get the selected voice record
            const selectedVoice = generatedVoices.find(v => v.id === selectedVoiceForAvatar);
            if (!selectedVoice) {
                throw new Error('找不到選定的語音記錄');
            }
            
            showAvatarGenerateModel(selectedVoice);
            
        } catch (error) {
            console.error('Generate digital avatar error:', error);
            window.dispatchEvent(new CustomEvent('showMessage', {
                detail: { type: 'error', text: error.message || '上傳語音資產失敗' }
            }));
            
            // Reset selection mode on error
            setIsSelectingVoice(false);
            setSelectedVoiceForAvatar(null);
        } finally {
            setIsGeneratingAvatar(false);
        }
    };
    try {
        return (
            <div 
                className="bg-white p-4 border border-gray-200 rounded-md"
                data-name={`rewrite-result-${index}`}
            >
                <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full mr-2">
                            {result.style}
                        </span>
                        <span className="text-xs text-gray-500">
                            {new Date(result.create_time).toLocaleString('zh-TW')}
                        </span>
                        {result.update_time && (
                            <span className="text-xs text-gray-500 ml-2">
                                (已編輯於 {new Date(result.update_time).toLocaleString('zh-TW')})
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        {isEditing ? (
                            <Fragment>
                                <button
                                    onClick={onSave}
                                    className="text-sm px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors flex items-center"
                                    disabled={isSaving}
                                    data-name={`save-rewrite-button-${index}`}
                                >
                                    {isSaving ? (
                                        <Fragment>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-1"></div>
                                            儲存中...
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            <i className="fas fa-save mr-1"></i>
                                            儲存
                                        </Fragment>
                                    )}
                                </button>
                                <button
                                    onClick={onEditToggle}
                                    className="text-sm px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
                                    disabled={isSaving}
                                    data-name={`cancel-rewrite-button-${index}`}
                                >
                                    <i className="fas fa-times mr-1"></i>
                                    取消
                                </button>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <button
                                    onClick={onEditToggle}
                                    className="text-sm px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                    data-name={`edit-rewrite-button-${index}`}
                                >
                                    <i className="fas fa-edit"></i>
                                </button>
                                <button
                                    onClick={() => onCopy(result.content)}
                                    className="text-gray-500 hover:text-gray-700"
                                    data-name={`copy-rewrite-button-${index}`}
                                >
                                    <i className="fas fa-copy"></i>
                                </button>
                            </Fragment>
                        )}
                    </div>
                </div>
                {isEditing ? (
                    <div>
                        <textarea
                            value={editedContent}
                            onChange={onContentChange}
                            className="w-full p-4 border border-gray-300 rounded-md h-64 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 mt-2"
                            disabled={isSaving}
                            data-name={`rewrite-content-editor-${index}`}
                        />
                        <div className="flex justify-end space-x-2 mt-3">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCopy(editedContent);
                                }}
                                className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors flex items-center"
                                data-name="copy-edited-button"
                            >
                                <i className="fas fa-copy mr-2"></i>
                                複製內容
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="whitespace-pre-wrap text-gray-700">
                            {result.content}
                        </div>
                        <div className="flex justify-end space-x-2 mt-3">
                            <button
                                onClick={(e) => openVoiceSettingsModal(e, result.id)}
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center"
                                data-name={`generate-audio-button-${index}`}
                            >
                                <i className="fas fa-volume-up mr-2"></i>
                                生成 AI 語音
                            </button>
                            <button
                                onClick={openVoiceModal}
                                className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors flex items-center"
                                data-name={`voice-clone-button-${index}`}
                            >
                                <i className="fas fa-microphone mr-2"></i>
                                語音克隆
                            </button>
                            <button
                                onClick={generateDigitalAvatar}
                                type="button"
                                className={`px-4 py-2 ${isSelectingVoice ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-500 hover:bg-green-600'} text-white rounded transition-colors flex items-center`}
                                disabled={isGeneratingAvatar}
                                data-name="generate-avatar-button"
                            >
                                {isGeneratingAvatar ? (
                                    <Fragment>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
                                        處理中...
                                    </Fragment>
                                ) : isSelectingVoice ? (
                                    <Fragment>
                                        <i className="fas fa-check mr-2"></i>
                                        確認選擇
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <i className="fas fa-user-circle mr-2"></i>
                                        生成數字人
                                    </Fragment>
                                )}
                            </button>
                            <button
                                onClick={() => onCopy(result.content)}
                                className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors flex items-center"
                                data-name={`copy-button-${index}`}
                            >
                                <i className="fas fa-copy mr-2"></i>
                                複製內容
                            </button>
                        </div>
                    </div>
                )}

                <VoiceRecordsList
                    generatedVoices={generatedVoices}
                    isSelectingVoice={isSelectingVoice}
                    selectedVoiceForAvatar={selectedVoiceForAvatar}
                    onVoiceSelect={handleVoiceSelect}
                    onCancelSelection={cancelVoiceSelection}
                    onCopy={onCopy}
                    currentPlayingId={currentPlayingId}
                    isPlaying={isPlaying}
                    onPlayVoice={onPlayVoice}
                />
            </div>
        );
    } catch (error) {
        console.error('RewriteResultItem component error:', error);
        reportError(error);
        return null;
    }
}

export default RewriteResultItem;